import React from 'react';
import { FaExclamationTriangle, FaSpinner, FaUsers, FaTimes, FaUser, FaTruck } from 'react-icons/fa';
import { ViewMode } from '../hooks/usePlantafel';
import PlantafelGanttRow from './PlantafelGanttRow';
import PlantafelProjectsTimeline from './PlantafelProjectsTimeline';
import { PlantafelGridHeaders } from './PlantafelGridHeaders';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { Shift, AssignedEmployee } from '../../shifts/services/ShiftService';
import { LaneTeam, LaneGroup, ProjectTimelineItem, TeamAsset } from '../services/PlantafelService';
import styles from './PlantafelBoard.module.css';

const LABEL_WIDTH = 120;

interface PlantafelBoardGridProps {
  error: string | null;
  loading: boolean;
  hasTeams: boolean;
  shifts: Shift[];
  laneGroups: LaneGroup[];
  waSimple: LaneGroup[];
  waComplex: LaneGroup[];
  filteredShifts: Shift[];
  taskMap: Map<number, CrmTask>;
  weekStart: Date;
  weekStartStr: string;
  days: any[];
  colWidth: number;
  gridWidth: number;
  settings: any;
  hourMarkers: number[];
  viewMode: ViewMode;
  absences: any[];
  absenceCountsByDate: Record<string, { approved: number; requested: number }>;
  activeEmployeeId: number | null;
  teamDropTargetId: number | null;
  highlightedShiftId: number | null;
  showProjectsTimeline: boolean;
  projectsTimeline: ProjectTimelineItem[];
  allProjects: ProjectTimelineItem[];
  selectedProjectId: number | null;
  refresh: () => Promise<void>;
  handleShiftClick: (shift: Shift, task: CrmTask | null) => void;
  moveShiftToTime: any;
  resizeShift: any;
  rescheduleShift: any;
  handleDropTask: (taskId: number, dayIndex: number, teamId: number) => Promise<void>;
  handleAssignEmployee: (shiftId: number, employeeId: number, employeeName?: string, fromShiftId?: number) => Promise<void>;
  handleAssignAsset: (shiftId: number, assetId: number) => Promise<void>;
  handleUnscheduleShiftDrop: (shiftId: number) => Promise<void>;
  handleCreateJobClick: (teamId: number, dateStr: string) => void;
  handleEditRoster: (team: LaneTeam) => void;
  handleAddTeamMember: (teamId: number, employeeId: number, employeeName?: string) => Promise<void>;
  handleRemoveTeamMember: (teamId: number, employeeId: number) => Promise<void>;
  handleAddTeamAsset: (teamId: number, assetId: number, assetName?: string) => Promise<void>;
  handleRemoveTeamAsset: (teamId: number, assetId: number) => Promise<void>;
  setTeamDropTargetId: (id: number | null) => void;
  setSelectedProjectId: (id: number | null) => void;
  handleCreateProject: () => void;
  handleDropProject: (projectId: number, dateStr: string, durationDays?: number) => Promise<void>;
  handleOpenProject: (projectId: number) => void;
  setAbsModal: (state: { open: boolean; date?: string; type?: 'approved' | 'requested'; anchor?: { top: number; left: number } }) => void;
}

export function PlantafelBoardGrid({
  error,
  loading,
  hasTeams,
  shifts,
  laneGroups,
  waSimple,
  waComplex,
  filteredShifts,
  taskMap,
  weekStart,
  weekStartStr,
  days,
  colWidth,
  gridWidth,
  settings,
  hourMarkers,
  viewMode,
  absences,
  absenceCountsByDate,
  activeEmployeeId,
  teamDropTargetId,
  highlightedShiftId,
  showProjectsTimeline,
  projectsTimeline,
  allProjects,
  selectedProjectId,
  refresh,
  handleShiftClick,
  moveShiftToTime,
  resizeShift,
  rescheduleShift,
  handleDropTask,
  handleAssignEmployee,
  handleAssignAsset,
  handleUnscheduleShiftDrop,
  handleCreateJobClick,
  handleEditRoster,
  handleAddTeamMember,
  handleRemoveTeamMember,
  handleAddTeamAsset,
  handleRemoveTeamAsset,
  setTeamDropTargetId,
  setSelectedProjectId,
  handleCreateProject,
  handleDropProject,
  handleOpenProject,
  setAbsModal,
}: PlantafelBoardGridProps): React.JSX.Element {
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <FaExclamationTriangle size={32} className={styles.errorIcon} />
        <div className={styles.errorTitle}>Fehler beim Laden</div>
        <div className={styles.errorMessage}>{error}</div>
        <button onClick={refresh} className={styles.retryButton}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (loading && shifts.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <FaSpinner className="spin" style={{ marginRight: '8px' }} />
        Lade Plantafel...
      </div>
    );
  }

  if (!hasTeams) {
    return (
      <div className={styles.noTeamsContainer}>
        <FaUsers size={32} className={styles.noTeamsIcon} />
        <div className={styles.noTeamsTitle}>Keine Teams vorhanden</div>
        <div className={styles.noTeamsMessage}>
          Bitte zuerst ein Team in einem Arbeitsbereich anlegen,<br />
          um Jobs planen zu können.
        </div>
      </div>
    );
  }

  const COL_WIDTH = colWidth;

  const renderTeamDragHandlers = (team: LaneTeam) => ({
    onDragOver: (e: React.DragEvent) => {
      const types = Array.from(e.dataTransfer.types);
      const hasEmployee = types.some((t) => t.toLowerCase() === 'employeeid');
      const hasAsset = types.some((t) => t.toLowerCase() === 'assetid');
      if (hasEmployee || hasAsset) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }
    },
    onDragEnter: (e: React.DragEvent) => {
      const types = Array.from(e.dataTransfer.types);
      const hasEmployee = types.some((t) => t.toLowerCase() === 'employeeid');
      const hasAsset = types.some((t) => t.toLowerCase() === 'assetid');
      if (hasEmployee || hasAsset) {
        e.preventDefault();
        setTeamDropTargetId(team.id);
      }
    },
    onDragLeave: (e: React.DragEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setTeamDropTargetId(null);
      }
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setTeamDropTargetId(null);

      const employeeId = e.dataTransfer.getData('employeeId') || e.dataTransfer.getData('employeeid');
      const employeeName = e.dataTransfer.getData('employeeName') || e.dataTransfer.getData('employeename');
      const assetId = e.dataTransfer.getData('assetId') || e.dataTransfer.getData('assetid');
      const assetName = e.dataTransfer.getData('assetName') || e.dataTransfer.getData('assetname');
      if (employeeId) {
        handleAddTeamMember(team.id, parseInt(employeeId, 10), employeeName);
      } else if (assetId) {
        handleAddTeamAsset(team.id, parseInt(assetId, 10), assetName);
      }
    },
  });

  const renderStickyHeaders = () => (
    <PlantafelGridHeaders
      viewMode={viewMode}
      days={days}
      colWidth={COL_WIDTH}
      settings={settings}
      hourMarkers={hourMarkers}
      absences={absences}
      absenceCountsByDate={absenceCountsByDate}
      activeEmployeeId={activeEmployeeId}
      setAbsModal={setAbsModal}
    />
  );

  const renderSimpleLane = (group: LaneGroup) => {
    const actualTeamId = group.teams.length === 1 ? group.teams[0].id : group.workAreaId;
    const teamColor = group.teams.length === 1 ? group.teams[0].work_area_color : group.workAreaColor;
    const simpleTeam = group.teams.length === 1 ? group.teams[0] : null;

    const dragHandlers = simpleTeam ? renderTeamDragHandlers(simpleTeam) : {};

    return (
      <div key={group.workAreaId} className={styles.laneRow}>
        <div
          {...dragHandlers}
          data-testid={simpleTeam ? `team-dropzone-${simpleTeam.id}` : undefined}
          className={`${styles.simpleLabelCell} ${simpleTeam && teamDropTargetId === simpleTeam.id ? styles.simpleLabelDropTarget : ''}`}
          style={{ width: LABEL_WIDTH }}
        >
          <div className={styles.colorDot} style={{ background: teamColor || group.workAreaColor || '#7c3aed' }} />
          <span className={styles.simpleLabelName}>{group.workAreaName}</span>
        </div>

        <PlantafelGanttRow
          workArea={{ id: actualTeamId, name: group.workAreaName, color: teamColor || group.workAreaColor }}
          parentWorkAreaId={group.workAreaId}
          shifts={filteredShifts}
          taskMap={taskMap}
          weekStart={weekStart}
          weekStartStr={weekStartStr}
          days={days}
          colWidth={COL_WIDTH}
          settings={settings}
          hourMarkers={hourMarkers}
          includeRegularShifts={true}
          viewMode={viewMode}
          onShiftClick={handleShiftClick}
          onMoveShift={moveShiftToTime}
          onResizeShift={resizeShift}
          onRescheduleShift={rescheduleShift}
          onDropTask={(taskId, dayIndex, teamId) => handleDropTask(taskId, dayIndex, teamId || actualTeamId)}
          onAssignEmployee={handleAssignEmployee}
          onAssignAsset={handleAssignAsset}
          onUnscheduleShift={handleUnscheduleShiftDrop}
          onCreateJob={handleCreateJobClick}
          highlightedShiftId={highlightedShiftId}
        />
      </div>
    );
  };

  const renderComplexLane = (group: LaneGroup) => (
    <div key={group.workAreaId}>
      {group.teams.map((team: LaneTeam) => {
        const dragHandlers = renderTeamDragHandlers(team);
        return (
          <div key={team.id} className={styles.laneRow}>
            <div
              {...dragHandlers}
              data-testid={`team-dropzone-${team.id}`}
              className={`${styles.complexLabelCell} ${teamDropTargetId === team.id ? styles.complexLabelDropTarget : ''}`}
              style={{ width: LABEL_WIDTH }}
            >
              <div className={styles.complexLabelTopRow}>
                <div className={styles.complexLabelInfo}>
                  <div className={styles.colorDotSmall} style={{ background: team.work_area_color || group.workAreaColor || '#7c3aed' }} />
                  <div style={{ overflow: 'hidden' }}>
                    <div className={styles.complexWaName}>{group.workAreaName}</div>
                    <div className={styles.complexTeamName}>{team.name}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleEditRoster(team)}
                  title="Roster bearbeiten"
                  className={styles.rosterEditButton}
                >
                  <FaUsers size={10} />
                </button>
              </div>
              <div className={styles.rosterChipsArea}>
                {team.roster_members && team.roster_members.map((member) => (
                  <div
                    key={member.id}
                    data-testid={`team-member-chip-${member.id}`}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('employeeId', member.employee_id.toString());
                      e.dataTransfer.setData('employeeName', member.employee_name || `${member.first_name} ${member.last_name}`);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    className={styles.memberChip}
                    title={`${member.employee_name || `${member.first_name} ${member.last_name}`} - Drag auf Job`}
                  >
                    <FaUser size={8} />
                    <span>{(() => {
                      const fullName = member.employee_name || `${member.first_name || ''} ${member.last_name || ''}`;
                      const parts = fullName.trim().split(' ');
                      if (parts.length >= 2) {
                        return `${parts[0]} ${parts[parts.length - 1][0]}.`;
                      }
                      return fullName;
                    })()}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRemoveTeamMember(team.id, member.employee_id); }}
                      className={`${styles.chipRemoveButton} ${styles.chipRemoveButtonMember}`}
                      title="Aus Team entfernen"
                    >
                      <FaTimes size={6} />
                    </button>
                  </div>
                ))}
                {team.roster_assets && team.roster_assets.map((asset: TeamAsset) => (
                  <div
                    key={asset.id}
                    data-testid={`team-asset-chip-${asset.id}`}
                    className={styles.assetChip}
                    title={asset.asset_name || asset.asset_code || 'Asset'}
                  >
                    <FaTruck size={8} color="#6b7280" />
                    {(asset.license_plate || asset.asset_name || asset.asset_code || '?').slice(0, 8)}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRemoveTeamAsset(team.id, asset.asset_id); }}
                      className={`${styles.chipRemoveButton} ${styles.chipRemoveButtonAsset}`}
                      title="Aus Team entfernen"
                    >
                      <FaTimes size={6} />
                    </button>
                  </div>
                ))}
                {(!team.roster_members || team.roster_members.length === 0) &&
                 (!team.roster_assets || team.roster_assets.length === 0) && (
                  <div className={styles.rosterEmptyHint}>
                    MA/Assets hierhin ziehen
                  </div>
                )}
              </div>
            </div>

            <PlantafelGanttRow
              workArea={{ id: team.id, name: team.name, color: team.work_area_color }}
              parentWorkAreaId={group.workAreaId}
              shifts={filteredShifts}
              taskMap={taskMap}
              weekStart={weekStart}
              weekStartStr={weekStartStr}
              days={days}
              colWidth={COL_WIDTH}
              settings={settings}
              hourMarkers={hourMarkers}
              includeRegularShifts={true}
              viewMode={viewMode}
              onShiftClick={handleShiftClick}
              onMoveShift={moveShiftToTime}
              onResizeShift={resizeShift}
              onRescheduleShift={rescheduleShift}
              onDropTask={(taskId, dayIndex, teamId) => handleDropTask(taskId, dayIndex, teamId)}
              onAssignEmployee={handleAssignEmployee}
              onAssignAsset={handleAssignAsset}
              onUnscheduleShift={handleUnscheduleShiftDrop}
              onCreateJob={handleCreateJobClick}
              highlightedShiftId={highlightedShiftId}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ minWidth: gridWidth }}>
      {renderStickyHeaders()}
      {laneGroups.length === 0 ? (
        <div className={styles.noTeamsFound}>Keine Teams gefunden</div>
      ) : (
        <>
          {waSimple.map(renderSimpleLane)}
          {showProjectsTimeline && (
            <PlantafelProjectsTimeline
              projects={projectsTimeline}
              allProjects={allProjects}
              weekStart={weekStart}
              days={days}
              colWidth={COL_WIDTH}
              labelWidth={LABEL_WIDTH}
              selectedProjectId={selectedProjectId}
              onProjectClick={setSelectedProjectId}
              onCreateProject={handleCreateProject}
              onDropProject={handleDropProject}
              viewMode={viewMode}
              onOpenProject={handleOpenProject}
            />
          )}
          {waComplex.map(renderComplexLane)}
        </>
      )}
    </div>
  );
}
